import { CheckCircle2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useResendVerification } from '@/hooks/use-auth';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { useAuthStore, type AuthenticatedUser } from '@/stores/auth-store';

import { AuthLayout } from './auth-layout';

type VerifyPhase = 'missing-token' | 'verifying' | 'success' | 'failed';

/**
 * One in-flight verification per token for this tab.
 *
 * React Strict Mode remounts the page and resets hook state, which used to fire the
 * spend twice: the first call consumed the link, the second failed, and the fresh
 * mutation instance could sit idle while the UI still showed "Confirming your email".
 */
const verificationByToken = new Map<string, Promise<AuthenticatedUser>>();

const verifyWithToken = (token: string): Promise<AuthenticatedUser> => {
  const existing = verificationByToken.get(token);
  if (existing) return existing;

  const pending = api.post<AuthenticatedUser>('/auth/verify-email', { token }, { withoutChurch: true });
  verificationByToken.set(token, pending);
  return pending;
};

/**
 * Where the link in the confirmation email lands.
 *
 * The token is spent as soon as the page opens, so the only thing the person does
 * here is read the outcome. If it failed, the way out is on the screen — an expired
 * link with no "send me another" is a dead end, and this is a page people reach when
 * they are already stuck.
 */
export const VerifyEmailPage = () => {
  const { t } = useTranslation('auth');
  const { t: tErrors } = useTranslation('errors');
  const [params] = useSearchParams();
  const token = params.get('token');

  const user = useAuthStore((state) => state.user);
  const status = useAuthStore((state) => state.status);
  const setUser = useAuthStore((state) => state.setUser);
  const restore = useAuthStore((state) => state.restore);

  const resend = useResendVerification();

  const [phase, setPhase] = useState<VerifyPhase>(() => (token ? 'verifying' : 'missing-token'));
  const [failureMessage, setFailureMessage] = useState<string | null>(null);

  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  useEffect(() => {
    if (!token) {
      setPhase('missing-token');
      return;
    }

    let cancelled = false;
    setPhase('verifying');
    setFailureMessage(null);

    void verifyWithToken(token)
      .then((verified) => {
        if (cancelled) return;
        setUser(verified);
        setPhase('success');
      })
      .catch((err) => {
        if (cancelled) return;
        setFailureMessage(
          err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'),
        );
        setPhase('failed');
      });

    return () => {
      cancelled = true;
    };
  }, [token, setUser, tErrors]);

  const resendToSelf = () => {
    if (!user?.email) return;
    resend.mutate(user.email, {
      onSuccess: () => toast.success(t('verifyEmail.resent')),
    });
  };

  if (phase === 'missing-token' || (phase === 'failed' && !token)) {
    return (
      <AuthLayout title={t('verifyEmail.failedTitle')}>
        <div className="space-y-4">
          <div className="flex items-start gap-3 text-body text-fg-secondary">
            <XCircle className="mt-0.5 size-5 shrink-0 text-danger" aria-hidden />
            <p>{t('verifyEmail.failedDescription')}</p>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" asChild>
              <Link to="/sign-in">{t('verifyEmail.continue')}</Link>
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (phase === 'failed') {
    return (
      <AuthLayout title={t('verifyEmail.failedTitle')}>
        <div className="space-y-4">
          <div className="flex items-start gap-3 text-body text-fg-secondary">
            <XCircle className="mt-0.5 size-5 shrink-0 text-danger" aria-hidden />
            <p>{failureMessage ?? t('verifyEmail.failedDescription')}</p>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" asChild>
              <Link to="/sign-in">{t('verifyEmail.continue')}</Link>
            </Button>
            {user?.email ? (
              <Button onClick={resendToSelf} loading={resend.isPending}>
                {t('verifyEmail.resend')}
              </Button>
            ) : null}
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (phase === 'success') {
    return (
      <AuthLayout title={t('verifyEmail.successTitle')}>
        <div className="space-y-4">
          <div className="flex items-start gap-3 text-body text-fg-secondary">
            <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" aria-hidden />
            <p>{t('verifyEmail.successDescription')}</p>
          </div>

          <div className="flex justify-end">
            <Button asChild>
              <Link to="/home">{t('verifyEmail.continue')}</Link>
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('verifyEmail.title')}>
      <div className="flex items-center gap-3 text-body text-fg-secondary">
        <Spinner className="size-5" />
        <p>{t('verifyEmail.verifying')}</p>
      </div>
    </AuthLayout>
  );
};
